import { useEffect, useState, useCallback } from "react";
import { Bell, X, Check } from "lucide-react";
import api from "@/lib/api";

const PRIO = { urgente: "bg-bordeaux-700", haute: "bg-bronze", normale: "bg-anthracite/40", basse: "bg-anthracite/20" };

export const NotificationBell = ({ onOpenProject }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications?limit=60");
      setData(data);
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => { clearInterval(id); document.removeEventListener("keydown", onKey); };
  }, [load]);

  const markAll = async () => {
    await api.post("/admin/notifications/read", { all: true });
    load();
  };
  const markOne = async (id) => {
    await api.post("/admin/notifications/read", { ids: [id] });
    load();
  };

  const items = data.items.filter((i) => filter === "all" || (filter === "unread" ? !i.read : i.event_type.startsWith(filter)));

  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={() => { setOpen(!open); load(); }}
        className="relative p-2 rounded-rama-sm text-white/70 hover:text-ivory hover:bg-white/5 transition-colors">
        <Bell size={18} />
        {data.unread > 0 && (
          <span data-testid="notif-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-bordeaux-700 text-ivory text-[10px] font-semibold flex items-center justify-center">
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-panel"
            className="absolute right-0 mt-2 w-[min(92vw,26rem)] max-h-[70vh] overflow-y-auto z-50 bg-ivory text-anthracite rounded-rama-md shadow-2xl border border-black/10">
            <div className="sticky top-0 bg-ivory border-b border-black/10 px-4 py-3 flex items-center justify-between">
              <div className="font-display text-lg">Notifications</div>
              <div className="flex items-center gap-2">
                <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-bordeaux-700 hover:underline">Tout marquer lu</button>
                <button onClick={() => setOpen(false)} className="text-ink-soft hover:text-anthracite"><X size={16} /></button>
              </div>
            </div>
            <div className="px-4 py-2 flex gap-1.5 flex-wrap border-b border-black/5">
              {[["all", "Tout"], ["unread", "Non lus"], ["appointment", "Rendez-vous"], ["project", "Projets"], ["estimate", "Estimations"], ["callback", "Rappels"]].map(([v, l]) => (
                <button key={v} data-testid={`notif-filter-${v}`} onClick={() => setFilter(v)}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${filter === v ? "bg-anthracite text-ivory" : "text-ink-soft hover:bg-black/5"}`}>{l}</button>
              ))}
            </div>
            {items.length === 0 && <div className="px-4 py-8 text-sm text-ink-soft">Aucune notification.</div>}
            <ul className="divide-y divide-black/5">
              {items.map((n) => (
                <li key={n.id} data-testid={`notif-item-${n.id}`}
                  className={`px-4 py-3 flex gap-3 ${n.read ? "opacity-60" : ""} hover:bg-black/[0.02]`}>
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${PRIO[n.priority] || PRIO.normale}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-anthracite">{n.title}</div>
                    {n.body && <div className="text-xs text-ink-soft mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[0.68rem] text-ink-soft mt-1 flex items-center gap-2">
                      <span>{new Date(n.occurred_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      {n.project_id && onOpenProject && (
                        <button data-testid={`notif-open-${n.id}`} onClick={() => { markOne(n.id); setOpen(false); onOpenProject(n.project_id); }}
                          className="text-bordeaux-700 hover:underline">Ouvrir le projet</button>
                      )}
                    </div>
                  </div>
                  {!n.read && (
                    <button data-testid={`notif-read-${n.id}`} onClick={() => markOne(n.id)} title="Marquer lu"
                      className="text-ink-soft hover:text-bordeaux-700 shrink-0"><Check size={14} /></button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
